import { ArrowUpRight } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import ClipImage from "@/components/ClipImage";
import { useLang } from "@/i18n";

const HANDSHAKE =
  "https://images.unsplash.com/photo-1758599543129-5269a8f29e68?crop=entropy&cs=srgb&fm=jpg&q=90";
const IMG_2 =
  "https://images.unsplash.com/photo-1695970922375-34520eca9bed?crop=entropy&cs=srgb&fm=jpg&q=90";

const PILLARS = [
  {
    n: "01",
    title: { it: "Misurazione integrata", en: "Integrated measurement" },
    body: {
      it: "I sensori Aéràs alimentano direttamente il registro carbon di Carbonplai. Nessun dato manuale, nessuna approssimazione.",
      en: "Aéràs sensors feed directly into Carbonplai's carbon ledger. No manual data, no approximation.",
    },
  },
  {
    n: "02",
    title: { it: "Report trimestrali", en: "Quarterly reports" },
    body: {
      it: "Ogni cliente riceve un dossier con emissioni evitate, consumi elettrici e proposta di compensazione.",
      en: "Every client receives a dossier with avoided emissions, energy consumption and an offsetting proposal.",
    },
  },
  {
    n: "03",
    title: { it: "Compensazione Gold Standard", en: "Gold Standard offsetting" },
    body: {
      it: "Accesso a un portafoglio di progetti verificati: forestazione, energie rinnovabili, cattura CO₂.",
      en: "Access to a portfolio of verified projects: forestation, renewable energy, CO₂ capture.",
    },
  },
];

export default function Partner() {
  const { tr } = useLang();
  return (
    <div data-testid="page-partner" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-baseline justify-between mb-8 gap-4 flex-wrap">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A]">
            {tr({ it: "● PARTNER STRATEGICO", en: "● STRATEGIC PARTNER" })}
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#52525b]">EST. 2025 · CERTIFIED</div>
        </div>

        <h1 className="font-display text-[clamp(3rem,11vw,11rem)] leading-[0.86] tracking-tighter">
          <div><RevealLine delay={0.2}>Aéràs</RevealLine></div>
          <div>
            <RevealLine delay={0.35} className="font-editorial italic font-normal text-[#007A8A]">
              ×
            </RevealLine>{" "}
            <RevealLine delay={0.5}>Carbonplai.</RevealLine>
          </div>
        </h1>

        <Reveal delay={0.7} className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <p className="lg:col-span-6 lg:col-start-7 font-body text-lg text-[#3f3f46] leading-relaxed">
            {tr({
              it: "Un'alleanza strategica che unisce l'ingegneria della purificazione dell'aria e la piattaforma di carbon accounting. Ogni impianto Aéràs viene tracciato, misurato e compensato in termini di CO₂ equivalente. L'aria pulita diventa carbon-negative.",
              en: "A strategic alliance uniting air purification engineering and a carbon accounting platform. Every Aéràs system is tracked, measured and offset in CO₂ equivalent. Clean air becomes carbon-negative.",
            })}
          </p>
        </Reveal>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7] pt-16">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <ClipImage src={HANDSHAKE} alt="Partnership Aéràs Carbonplai" className="w-full aspect-[21/9] border border-[#e4e4e7]" />
        </div>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24">
          <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#e4e4e7]">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.05}>
                <div className="border-r border-b border-[#e4e4e7] p-10 min-h-[320px] bg-[#f4f4f5]">
                  <div className="font-editorial italic text-6xl text-[#3f3f46] mb-8">{p.n}</div>
                  <h3 className="font-display text-2xl md:text-3xl tracking-tighter mb-4">{tr(p.title)}</h3>
                  <p className="font-body text-sm text-[#3f3f46] leading-relaxed">{tr(p.body)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 border-r border-[#e4e4e7] p-8 md:p-16 lg:p-24 flex flex-col justify-center">
            <Reveal>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
                {tr({ it: "— MANIFESTO CONGIUNTO", en: "— JOINT MANIFESTO" })}
              </div>
              <blockquote className="font-editorial italic text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-[#0a0a0a] mb-10">
                {tr({
                  it: '"Non basta purificare l\'aria: bisogna renderla parte misurabile del bilancio ambientale di un\'azienda. Aria pulita e carbon-neutral non sono più concetti separati."',
                  en: '"Purifying the air is not enough: it must become a measurable part of a company\'s environmental balance. Clean air and carbon-neutral are no longer separate concepts."',
                })}
              </blockquote>
              <div className="font-mono text-[10px] tracking-widest uppercase text-[#52525b]">
                — {tr({ it: "Direzione", en: "Management" })} Aéràs Technology + Carbonplai
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 relative aspect-square lg:aspect-auto overflow-hidden">
            <ClipImage src={IMG_2} alt="Carbon-neutral" className="absolute inset-0" />
          </div>
        </div>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h4 className="font-display text-4xl md:text-6xl tracking-tighter max-w-3xl">
            {tr({ it: "Scopri di più sul nostro", en: "Learn more about our" })}{" "}
            <span className="font-editorial italic font-normal text-[#007A8A]">
              {tr({ it: "Partner strategico", en: "strategic Partner" })}
            </span>.
          </h4>
          <a
            href="https://carbonplai.com"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="partner-carbonplai-link"
            className="btn-primary"
          >
            carbonplai.com <ArrowUpRight size={14} />
          </a>
        </div>
      </section>
    </div>
  );
}
